import React, { useCallback, useRef, useState } from "react";
import { Platform, Pressable, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";

import CountBadge from "@/components/settings/CountBadge";
import { colors, radius, text } from "@/components/settings/theme";
import { useIsMobileUX } from "@/components/hooks/useIsMobileUX";
import { t, tn } from "@/localization";
import { useWhatsNew } from "./store";
import WhatsNewMenu from "./WhatsNewMenu";

// "What's new" in the dashboard header, business accounts only. A counter
// shows how many entries this broadcaster hasn't seen; with none unread it's
// a plain icon (and label on wide screens). Wide web layouts open a dropdown,
// phones and the apps open the What's new screen.
const WhatsNewButton = ({ style }) => {
  const wn = useWhatsNew();
  const navigation = useNavigation();
  const isMobileUX = useIsMobileUX();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  if (!wn.enabled || !wn.entries.length) return null;

  const dropdown = Platform.OS === "web" && !isMobileUX;
  const count = wn.ready ? wn.unreadCount : 0;
  const label = count
    ? tn(
        "What's new, {count} new update",
        "What's new, {count} new updates",
        count
      )
    : t("What's new");

  return (
    <>
      <Pressable
        ref={anchorRef}
        testID="whats-new-button"
        accessibilityRole="button"
        accessibilityLabel={label}
        aria-expanded={dropdown ? open : undefined}
        aria-haspopup={dropdown ? "dialog" : undefined}
        hitSlop={8}
        onPress={() =>
          dropdown
            ? setOpen((v) => !v)
            : navigation.navigate("WhatsNew", { source: "header" })
        }
        style={({ hovered, pressed }) => [
          {
            flexDirection: "row",
            alignItems: "center",
            borderRadius: radius.control,
            backgroundColor:
              (hovered || pressed || open) && !isMobileUX
                ? colors.pressOverlay
                : "transparent",
          },
          style,
        ]}
      >
        <View>
          <Ionicons
            name={count ? "sparkles" : "sparkles-outline"}
            size={isMobileUX ? 22 : 20}
            color={colors.slate}
          />
          {isMobileUX && <CountBadge count={count} overlay />}
        </View>
        {!isMobileUX && (
          <>
            <Text
              style={[text.bodyStrong, { color: colors.slate, marginLeft: 6 }]}
            >
              {t("What's new")}
            </Text>
            <CountBadge count={count} style={{ marginLeft: 6 }} />
          </>
        )}
      </Pressable>
      {dropdown && (
        <WhatsNewMenu anchorRef={anchorRef} open={open} onClose={close} />
      )}
    </>
  );
};

export default WhatsNewButton;
